import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  PiggyBank, 
  Home, 
  GraduationCap, 
  Car, 
  Smartphone, 
  TrendingUp,
  ArrowRight
} from "lucide-react";

const FeaturedProducts = () => {
  const products = [
    {
      icon: PiggyBank,
      title: "Member Deposits",
      description: "This is your primary, non-withdrawable savings account that forms the foundation of your Sacco membership. Your consistent monthly deposits not only build your savings but also determine your loan eligibility and earn you competitive annual interest.",
      rate: "Competitive annual interest",
      minAmount: "Monthly contributions",
      features: ["Foundation of membership", "Determines loan eligibility", "Competitive annual interest"],
      color: "bg-primary",
      popular: false
    },
    {
      icon: TrendingUp,
      title: "Share Capital",
      description: "Your Share Capital is your ownership stake in Natcon Sacco. It's a non-refundable investment that makes you a part-owner of our society, giving you voting rights and earning you annual dividends based on the Sacco's performance.",
      rate: "Annual dividends",
      minAmount: "KSh 5,000 minimum",
      features: ["Part-owner of society", "Voting rights", "Annual dividends based on performance"],
      color: "bg-primary",
      popular: false
    },
    {
      icon: Home,
      title: "Normal (Development) Loan",
      description: "This is our primary loan product, designed to fund your significant projects like land purchase, business investment, or home improvement.",
      rate: "1% per month (12% p.a.) on reducing balance",
      minAmount: "Up to 3x member savings",
      features: ["Up to 96 months repayment", "Minimum 3 guarantors required", "Apply by 10th for 5-day disbursement"],
      color: "bg-primary",
      popular: true
    },
    {
      icon: PiggyBank,
      title: "Emergency Loan",
      description: "When unforeseen circumstances arise, such as sudden hospitalization or funeral expenses, this loan provides fast access to funds.",
      rate: "1.5% per month (18% p.a.) on reducing balance",
      minAmount: "Up to KSh 100,000",
      features: ["Within 24 hours disbursement", "Up to 12 months repayment", "Apply even with existing loan"],
      color: "bg-destructive",
      popular: false
    },
    {
      icon: GraduationCap,
      title: "Education Loan",
      description: "Facilitate the payment of school, college, university, or professional fees with our flexible education loan. Note: Only one education loan may be granted per member in a calendar year.",
      rate: "1.25% per month (15% p.a.) on reducing balance",
      minAmount: "Up to KSh 300,000 (College/University)",
      features: ["Two tier system (Primary/Secondary: KSh 100k, College/University: KSh 300k)", "Up to 12 months repayment", "One loan per calendar year"],
      color: "bg-blue-500",
      popular: false
    },
    {
      icon: Smartphone,
      title: "Salary Advance",
      description: "Get an advance on your salary to handle immediate financial needs with quick processing and flexible terms.",
      rate: "2% per month (24% p.a.) on reducing balance",
      minAmount: "Up to KSh 150,000",
      features: ["Up to 3 months repayment", "Fast processing and approval", "Automatic payroll deduction"],
      color: "bg-green-500",
      popular: false
    }
  ];

  return (
    <section className="py-20 bg-background touch-pan-y" aria-labelledby="products-heading">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 id="products-heading" className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Our Featured Products & Services
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover our comprehensive range of financial products designed to meet your every need, 
            from savings and loans to investment opportunities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 isolate contain-paint" role="list">
          {products.map((product, index) => {
            const Icon = product.icon;
            return (
              <Card key={index} className="relative isolate transform-gpu will-change-transform border border-border/50 md:group md:hover:shadow-medium md:transition-shadow md:duration-300 md:hover:border-primary/20" role="listitem">
                {product.popular && (
                  <Badge className="absolute -top-2 right-4 bg-accent text-accent-foreground" aria-label="Most popular product">
                    Most Popular
                  </Badge>
                )}
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className={`h-12 w-12 rounded-lg ${product.color} flex items-center justify-center transform-gpu will-change-transform`} aria-hidden="true">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{product.title}</CardTitle>
                      <div className="text-sm font-semibold text-primary">{product.rate}</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {product.description}
                  </p>
                  
                  <div className="text-sm">
                    <span className="font-medium text-foreground">Maximum: </span>
                    <span className="text-muted-foreground">{product.minAmount}</span>
                  </div>

                  <ul className="space-y-2">
                    {product.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    variant="outline" 
                    className="w-full md:group-hover:bg-primary md:group-hover:text-primary-foreground md:transition-colors"
                    asChild
                  >
                    <a href="/products#loans" aria-label={`Learn more about ${product.title}`}>
                      Learn About {product.title}
                      <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button size="lg" className="bg-gradient-primary hover:opacity-90" asChild>
            <a href="/products">
              View All Products & Services
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;